import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { UPDATE_PERSON_MUTATION } from "@/features/person/queries";
import TextField from "@/components/TextField";
import TipCard from "@/components/TipCard";
import { emailPattern } from "@/lib/emailHelpers";
import { EditProps } from "./types";
import { useNavigate } from "@/router";

type FieldValues = {
  email: string;
};

interface EmailEditModalProps extends EditProps {
  email?: string;
}

const defaultErrorMessage = "Failed to update email";

export default function EmailEditForm({ personId, email = "", onCancel, onSuccess, onError }: EmailEditModalProps) {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setError,
    getValues
  } = useForm({
    defaultValues: { email },
    mode: "onChange"
  });

  const navigate = useNavigate();
  const [updatePerson, { loading }] = useMutation(UPDATE_PERSON_MUTATION);
  const emailIsTaken = errors.email?.type === "409";

  async function onSubmit({ email }: FieldValues) {
    try {
      const input = {
        id: personId,
        email
      };

      const { data } = await updatePerson({
        variables: { input }
      });
      const {
        updatePerson: { success, person, code, message }
      } = data;

      if (success) {
        return onSuccess(person);
      }
      if (code === 409) {
        return setError("email", { type: code.toString(), message: "Person already exists with this email" });
      }

      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="email-edit-form">
      <Controller
        control={control}
        name="email"
        rules={{
          required: "Email is required",
          pattern: { value: emailPattern, message: "The email address is invalid" }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Email"
            error={!!errors.email}
            errorText={errors.email?.message}
          />
        )}
      />

      {emailIsTaken && (
        <TipCard
          onClick={() => navigate({ pathname: "/search", search: `q=${encodeURIComponent(getValues("email"))}` })}
        />
      )}

      <div className="bg-extended-warning-brand-color-container py-3 px-6">
        <p>Updating this email does NOT automatically update this person&#39;s Auth0 account login.</p>

        <div className="w-fit mx-auto py-2.5 px-4">
          <Button
            variant="text"
            icon="open_in_new"
            theme="info"
            onClick={() => window.open("https://manage.auth0.com/dashboard/us/prod-pga/users", "_blank")}
          >
            Update in Auth0
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading || !isValid} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
